import type {
  EnvironmentId,
  ScopedThreadRef,
  ServerAgentStatus,
  ThreadId,
  TurnId,
} from "@t3tools/contracts";
import { scopedThreadKey } from "@t3tools/client-runtime";
import {
  PROVIDER_SEND_TURN_MAX_ATTACHMENTS,
  PROVIDER_SEND_TURN_MAX_FILE_BYTES,
  PROVIDER_SEND_TURN_MAX_IMAGE_BYTES,
} from "@t3tools/contracts";
import { serializeComposerFileLink } from "@t3tools/shared/composerTrigger";
import {
  Fragment,
  memo,
  type ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  clampCollapsedComposerCursor,
  collapseExpandedComposerCursor,
  detectComposerTrigger,
  expandCollapsedComposerCursor,
  insertTranscriptAtCursor,
  replaceTextRange,
  type ComposerTrigger,
} from "../../composer-logic";
import { deriveComposerSendState, readFileAsDataUrl } from "../ChatView.logic";
import {
  type ComposerFileAttachment,
  type ComposerImageAttachment,
  type DraftId,
  type PersistedComposerImageAttachment,
  useComposerDraftStore,
  useComposerThreadDraft,
} from "../../composerDraftStore";
import { useComposerPathSearch } from "../../lib/composerPathSearchState";
import { shouldUseCompactComposerFooter } from "../composerFooterLayout";
import { type ComposerPromptEditorHandle, ComposerPromptEditor } from "../ComposerPromptEditor";
import { useFeature, useVoiceTranscriptionEnabled } from "../../rpc/serverState";
import { type ComposerCommandItem, ComposerCommandMenu } from "./ComposerCommandMenu";
import { CompactComposerControlsMenu } from "./CompactComposerControlsMenu";
import { ComposerPrimaryActions } from "./ComposerPrimaryActions";
import {
  type ComposerVoiceInputControls,
  resolveVoiceInputAvailability,
} from "./ComposerVoiceButton";
import { type ComposerVoiceBarMode, ComposerVoiceRecordingBar } from "./ComposerVoiceRecordingBar";
import { resolveComposerMenuActiveItemId } from "./composerMenuHighlight";
import { searchSlashCommandItems } from "./composerSlashCommandSearch";
import { ContextWindowMeter } from "./ContextWindowMeter";
import { type ExpandedImagePreview } from "./ExpandedImagePreview";
import { AttachmentsBlock } from "./AttachmentsBlock";
import { basenameOfPath } from "../../vscode-icons";
import { cn, randomUUID } from "~/lib/utils";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { toastManager } from "../ui/toast";
import { ListTodoIcon, PaperclipIcon } from "lucide-react";
import type { SessionPhase, Thread } from "../../types";
import { deriveLatestContextWindowSnapshot } from "../../lib/contextWindow";
import { formatProviderSkillDisplayName } from "../../providerSkillPresentation";
import { searchProviderSkills } from "../../providerSkillSearch";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useVoiceInput } from "../../hooks/useVoiceInput";
import type { VoiceClip } from "../../voiceClips/voiceClipController";
import { useVoiceClipAudio, useVoiceClips, voiceClips } from "../../voiceClips/voiceClipStore";

import { Trans, useLingui } from "@lingui/react/macro";
import * as Schema from "effect/Schema";

const DEEP_WORK_INTRO_SEEN_KEY = "t3code:deep-work-intro-seen";

const IMAGE_SIZE_LIMIT_LABEL = `${Math.round(PROVIDER_SEND_TURN_MAX_IMAGE_BYTES / (1024 * 1024))}MB`;
const FILE_SIZE_LIMIT_LABEL = `${Math.round(PROVIDER_SEND_TURN_MAX_FILE_BYTES / (1024 * 1024))}MB`;

const COMPOSER_FLOATING_LAYER_SELECTOR = [
  '[data-slot="popover-popup"]',
  '[data-slot="menu-popup"]',
  '[data-slot="select-popup"]',
  '[data-slot="combobox-popup"]',
  '[data-slot="autocomplete-popup"]',
].join(",");

const extendReplacementRangeForTrailingSpace = (
  text: string,
  rangeEnd: number,
  replacement: string,
): number => {
  if (!replacement.endsWith(" ")) {
    return rangeEnd;
  }
  return text[rangeEnd] === " " ? rangeEnd + 1 : rangeEnd;
};

function isInsideComposerFloatingLayer(element: Element): boolean {
  return element.closest(COMPOSER_FLOATING_LAYER_SELECTOR) !== null;
}

const ComposerFooterModeControls = memo(function ComposerFooterModeControls(props: {
  showPlanToggle: boolean;
  planSidebarLabel: string;
  planSidebarOpen: boolean;
  showDeepWorkToggle: boolean;
  deepWorkEnabled: boolean;
  onToggleDeepWork: () => void;
  onTogglePlanSidebar: () => void;
}) {
  const { t } = useLingui();
  const planSidebarLabel = props.planSidebarLabel.toLowerCase();
  const planSidebarTooltip = props.planSidebarOpen
    ? t`Hide ${planSidebarLabel} sidebar`
    : t`Show ${planSidebarLabel} sidebar`;

  const deepWorkTooltip = props.deepWorkEnabled
    ? t`Deep Work enabled — click to disable`
    : t`Deep Work disabled — click to enable`;

  const sections: Array<{ key: string; node: ReactNode }> = [];

  if (props.showDeepWorkToggle) {
    sections.push({
      key: "deep-work",
      node: (
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant="ghost"
                className={cn(
                  "shrink-0 whitespace-nowrap px-2 sm:px-3",
                  props.deepWorkEnabled
                    ? "bg-blue-500/10 text-blue-400 hover:bg-blue-500/15 hover:text-blue-300"
                    : "text-muted-foreground/70 hover:text-foreground/80",
                )}
                size="sm"
                type="button"
                onClick={props.onToggleDeepWork}
                aria-label={deepWorkTooltip}
              />
            }
          >
            <Trans>Deep Work</Trans>
          </TooltipTrigger>
          <TooltipPopup side="top">{deepWorkTooltip}</TooltipPopup>
        </Tooltip>
      ),
    });
  }

  if (props.showPlanToggle) {
    sections.push({
      key: "plan-sidebar",
      node: (
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant="ghost"
                className={cn(
                  "shrink-0 whitespace-nowrap px-2 sm:px-3",
                  props.planSidebarOpen
                    ? "bg-blue-500/10 text-blue-400 hover:bg-blue-500/15 hover:text-blue-300"
                    : "text-muted-foreground/70 hover:text-foreground/80",
                )}
                size="sm"
                type="button"
                onClick={props.onTogglePlanSidebar}
                aria-label={planSidebarTooltip}
              />
            }
          >
            <ListTodoIcon
              className={props.planSidebarOpen ? "text-current opacity-100" : undefined}
            />
            <span className="sr-only sm:not-sr-only">{props.planSidebarLabel}</span>
          </TooltipTrigger>
          <TooltipPopup side="top">{planSidebarTooltip}</TooltipPopup>
        </Tooltip>
      ),
    });
  }

  return (
    <>
      {sections.map((section) => (
        <Fragment key={section.key}>{section.node}</Fragment>
      ))}
    </>
  );
});

const ComposerAttachFilesButton = memo(function ComposerAttachFilesButton(props: {
  disabled: boolean;
  onClick: () => void;
}) {
  const { t } = useLingui();
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            className="shrink-0 px-2 text-muted-foreground/70 hover:text-foreground/80"
            size="sm"
            type="button"
            disabled={props.disabled}
            onClick={props.onClick}
            aria-label={t`Attach files`}
          />
        }
      >
        <PaperclipIcon />
      </TooltipTrigger>
      <TooltipPopup side="top">{t`Attach files`}</TooltipPopup>
    </Tooltip>
  );
});

const ComposerFooterPrimaryActions = memo(function ComposerFooterPrimaryActions(props: {
  activeContextWindow: ReturnType<typeof deriveLatestContextWindowSnapshot>;
  isUploading: boolean;
  isRunning: boolean;
  isSendBusy: boolean;
  isConnecting: boolean;
  isEnvironmentUnavailable: boolean;
  isBlocked: boolean;
  hasSendableContent: boolean;
  voiceInput: ComposerVoiceInputControls | null;
  preserveComposerFocusOnPointerDown?: boolean;
  onInterrupt: () => void;
}) {
  return (
    <>
      {props.activeContextWindow ? <ContextWindowMeter usage={props.activeContextWindow} /> : null}
      <ComposerPrimaryActions
        isRunning={props.isRunning}
        isSendBusy={props.isSendBusy}
        isConnecting={props.isConnecting}
        isEnvironmentUnavailable={props.isEnvironmentUnavailable}
        isBlocked={props.isBlocked}
        isUploading={props.isUploading}
        hasSendableContent={props.hasSendableContent}
        voiceInput={props.voiceInput}
        preserveComposerFocusOnPointerDown={props.preserveComposerFocusOnPointerDown ?? false}
        onInterrupt={props.onInterrupt}
      />
    </>
  );
});

// --------------------------------------------------------------------------
// Handle exposed to ChatView
// --------------------------------------------------------------------------

export interface ChatComposerHandle {
  focusAtEnd: () => void;
  focusAt: (cursor: number) => void;
  insertTextAtEnd: (text: string) => boolean;
  /** Replace the whole prompt with `text` and place the cursor at its end. */
  replacePrompt: (text: string) => boolean;
  readSnapshot: () => {
    value: string;
    cursor: number;
    expandedCursor: number;
  };
  /** Reset composer cursor/trigger/highlight after external prompt mutations (e.g. onSend). */
  resetCursorState: (options?: {
    cursor?: number;
    prompt?: string;
    detectTrigger?: boolean;
  }) => void;
  /** Get the current prompt, attachments and Deep Work state for use in send. */
  getSendContext: () => {
    prompt: string;
    images: ComposerImageAttachment[];
    files: ComposerFileAttachment[];
    deepWork: boolean;
  };
}

// --------------------------------------------------------------------------
// Props
// --------------------------------------------------------------------------

export interface ChatComposerProps {
  composerDraftTarget: ScopedThreadRef | DraftId;
  environmentId: EnvironmentId;
  draftId: DraftId | null;

  // Thread context
  activeThreadId: ThreadId | null;
  activeThreadEnvironmentId: EnvironmentId | undefined;
  isServerThread: boolean;
  isLocalDraftThread: boolean;

  // Session phase
  phase: SessionPhase;
  isConnecting: boolean;
  isSendBusy: boolean;
  isUploading: boolean;
  environmentUnavailable: {
    readonly label: string;
    readonly connectionState: "connecting" | "disconnected" | "error";
  } | null;
  blocked: boolean;

  // Plan
  activePlan: { turnId?: TurnId } | null;
  planSidebarOpen: boolean;

  agentStatus: ServerAgentStatus | null;

  // Context window
  activeThreadActivities: Thread["activities"] | undefined;

  // Misc
  resolvedTheme: "light" | "dark";
  projectCwd: string | null;

  // Refs the parent needs kept in sync
  promptRef: React.RefObject<string>;
  composerImagesRef: React.RefObject<ComposerImageAttachment[]>;
  composerFileAttachmentsRef: React.RefObject<ComposerFileAttachment[]>;
  composerRef: React.RefObject<ChatComposerHandle | null>;

  // Scroll
  shouldAutoScrollRef: React.RefObject<boolean>;
  scheduleStickToBottom: () => void;

  // Callbacks
  onSend: (e?: { preventDefault: () => void }) => void;
  onInterrupt: () => void;

  togglePlanSidebar: () => void;

  focusComposer: () => void;
  setThreadError: (threadId: ThreadId | null, error: string | null) => void;
  onExpandImage: (preview: ExpandedImagePreview) => void;
}

// --------------------------------------------------------------------------
// Component
// --------------------------------------------------------------------------

export const ChatComposer = memo(function ChatComposer(props: ChatComposerProps) {
  const { t } = useLingui();
  const {
    composerDraftTarget,
    environmentId,
    draftId,
    activeThreadId,
    activeThreadEnvironmentId: _activeThreadEnvironmentId,
    isServerThread: _isServerThread,
    isLocalDraftThread: _isLocalDraftThread,
    phase,
    isConnecting,
    isSendBusy,
    isUploading,
    environmentUnavailable,
    blocked,
    activePlan,
    planSidebarOpen,
    agentStatus,
    activeThreadActivities,
    resolvedTheme,
    projectCwd,
    promptRef,
    composerRef,
    composerImagesRef,
    composerFileAttachmentsRef,
    shouldAutoScrollRef,
    scheduleStickToBottom,
    onSend,
    onInterrupt,
    togglePlanSidebar,
    focusComposer,
    setThreadError,
    onExpandImage,
  } = props;
  const inputUnavailable = environmentUnavailable !== null || blocked;

  // ------------------------------------------------------------------
  // Store subscriptions (prompt / images)
  // ------------------------------------------------------------------
  const composerDraft = useComposerThreadDraft(composerDraftTarget);
  const prompt = composerDraft.prompt;
  const composerImages = composerDraft.images;
  const composerFileAttachments = composerDraft.fileAttachments;
  const nonPersistedComposerImageIds = composerDraft.nonPersistedImageIds;

  const setComposerDraftPrompt = useComposerDraftStore((store) => store.setPrompt);
  const addComposerDraftImage = useComposerDraftStore((store) => store.addImage);
  const addComposerDraftImages = useComposerDraftStore((store) => store.addImages);
  const removeComposerDraftImage = useComposerDraftStore((store) => store.removeImage);
  const addComposerDraftFileAttachments = useComposerDraftStore(
    (store) => store.addFileAttachments,
  );
  const removeComposerDraftFileAttachment = useComposerDraftStore(
    (store) => store.removeFileAttachment,
  );
  const clearComposerDraftPersistedAttachments = useComposerDraftStore(
    (store) => store.clearPersistedAttachments,
  );
  const syncComposerDraftPersistedAttachments = useComposerDraftStore(
    (store) => store.syncPersistedAttachments,
  );
  const getComposerDraft = useComposerDraftStore((store) => store.getComposerDraft);

  // ------------------------------------------------------------------
  // Deep Work
  // ------------------------------------------------------------------
  const deepWorkEnabled = composerDraft.deepWork;
  const [deepWorkIntroOpen, setDeepWorkIntroOpen] = useState(false);
  const [deepWorkIntroSeen, setDeepWorkIntroSeen] = useLocalStorage(
    DEEP_WORK_INTRO_SEEN_KEY,
    false,
    Schema.Boolean,
  );
  const setComposerDraftDeepWork = useComposerDraftStore((store) => store.setDeepWork);
  const toggleDeepWork = useCallback(() => {
    setComposerDraftDeepWork(composerDraftTarget, !deepWorkEnabled);
  }, [composerDraftTarget, deepWorkEnabled, setComposerDraftDeepWork]);
  const requestToggleDeepWork = useCallback(() => {
    if (!deepWorkEnabled && !deepWorkIntroSeen) {
      setDeepWorkIntroOpen(true);
      return;
    }
    toggleDeepWork();
  }, [deepWorkEnabled, deepWorkIntroSeen, toggleDeepWork]);
  const confirmDeepWorkIntro = useCallback(() => {
    setDeepWorkIntroSeen(true);
    setDeepWorkIntroOpen(false);
    toggleDeepWork();
  }, [setDeepWorkIntroSeen, toggleDeepWork]);
  // ------------------------------------------------------------------
  // Context window
  // ------------------------------------------------------------------
  const activeContextWindow = useMemo(
    () => deriveLatestContextWindowSnapshot(activeThreadActivities ?? []),
    [activeThreadActivities],
  );

  const deepWorkFeatureEnabled = useFeature("deepWork");
  const voiceInputEnabled = useFeature("voiceInput");
  const voiceTranscriptionEnabled = useVoiceTranscriptionEnabled();

  // ------------------------------------------------------------------
  // Composer-local state
  // ------------------------------------------------------------------
  const [composerCursor, setComposerCursor] = useState(() =>
    collapseExpandedComposerCursor(prompt, prompt.length),
  );
  const [composerTrigger, setComposerTrigger] = useState<ComposerTrigger | null>(() =>
    detectComposerTrigger(prompt, prompt.length),
  );
  const [composerHighlightedItemId, setComposerHighlightedItemId] = useState<string | null>(null);
  const [composerHighlightedSearchKey, setComposerHighlightedSearchKey] = useState<string | null>(
    null,
  );
  const [isDragOverComposer, setIsDragOverComposer] = useState(false);
  const [isComposerFooterCompact, setIsComposerFooterCompact] = useState(false);
  const [isComposerFocused, setIsComposerFocused] = useState(false);
  const isMobileViewport = useMediaQuery("max-sm");
  const isComposerCollapsedMobile = isMobileViewport && !isComposerFocused;
  const voiceTargetKey = useMemo(
    () =>
      typeof composerDraftTarget === "string"
        ? composerDraftTarget
        : scopedThreadKey(composerDraftTarget),
    [composerDraftTarget],
  );
  const composerVoiceClips = useVoiceClips(voiceTargetKey);
  const voiceInput = useVoiceInput({
    environmentId,
    targetKey: voiceTargetKey,
  });

  // ------------------------------------------------------------------
  // Refs
  // ------------------------------------------------------------------
  const composerEditorRef = useRef<ComposerPromptEditorHandle>(null);
  const composerFormRef = useRef<HTMLFormElement>(null);
  const composerSurfaceRef = useRef<HTMLDivElement>(null);
  const composerFormHeightRef = useRef(0);
  const composerSelectLockRef = useRef(false);
  const composerMenuOpenRef = useRef(false);
  const composerMenuItemsRef = useRef<ComposerCommandItem[]>([]);
  const activeComposerMenuItemRef = useRef<ComposerCommandItem | null>(null);
  const composerBlurFrameRef = useRef<number | null>(null);
  const mobileComposerExpandFrameRef = useRef<number | null>(null);
  const mobileComposerExpandReleaseFrameRef = useRef<number | null>(null);
  const mobileComposerExpandInFlightRef = useRef(false);
  const dragDepthRef = useRef(0);
  const attachFilesInputRef = useRef<HTMLInputElement>(null);

  // ------------------------------------------------------------------
  // Derived: composer send state
  // ------------------------------------------------------------------
  const composerSendState = useMemo(
    () =>
      deriveComposerSendState({
        prompt,
        imageCount: composerImages.length + composerFileAttachments.length,
      }),
    [composerFileAttachments.length, composerImages.length, prompt],
  );

  // ------------------------------------------------------------------
  // Derived: composer trigger / menu
  // ------------------------------------------------------------------
  const composerTriggerKind = composerTrigger?.kind ?? null;
  const pathTriggerQuery = composerTrigger?.kind === "path" ? composerTrigger.query : "";
  const isPathTrigger = composerTriggerKind === "path";
  const workspaceEntries = useComposerPathSearch({
    environmentId,
    cwd: isPathTrigger ? projectCwd : null,
    query: isPathTrigger ? pathTriggerQuery : null,
  });

  const composerMenuItems = useMemo<ComposerCommandItem[]>(() => {
    if (!composerTrigger) return [];
    if (composerTrigger.kind === "path") {
      return workspaceEntries.entries.map((entry) => ({
        id: `path:${entry.kind}:${entry.path}`,
        type: "path",
        path: entry.path,
        pathKind: entry.kind,
        label: basenameOfPath(entry.path),
        description: entry.parentPath ?? "",
      }));
    }
    if (composerTrigger.kind === "slash-command") {
      const providerSlashCommandItems = (agentStatus?.slashCommands ?? []).map((command) => ({
        id: `provider-slash-command:${command.name}`,
        type: "provider-slash-command" as const,
        command,
        label: `/${command.name}`,
        description: command.description ?? command.input?.hint ?? t`Run provider command`,
      }));
      const query = composerTrigger.query.trim().toLowerCase();
      if (!query) {
        return providerSlashCommandItems;
      }
      return searchSlashCommandItems(providerSlashCommandItems, query);
    }
    if (composerTrigger.kind === "skill") {
      return searchProviderSkills(agentStatus?.skills ?? [], composerTrigger.query).map(
        (skill) => ({
          id: `skill:${skill.name}`,
          type: "skill" as const,
          skill,
          label: formatProviderSkillDisplayName(skill),
          description:
            skill.shortDescription ??
            skill.description ??
            (skill.scope ? t`${skill.scope} skill` : t`Run provider skill`),
        }),
      );
    }
    return [];
  }, [agentStatus, composerTrigger, t, workspaceEntries.entries]);

  const composerMenuOpen = Boolean(composerTrigger);
  const composerMenuSearchKey = composerTrigger
    ? `${composerTrigger.kind}:${composerTrigger.query.trim().toLowerCase()}`
    : null;
  const activeComposerMenuItem = useMemo(() => {
    const activeItemId = resolveComposerMenuActiveItemId({
      items: composerMenuItems,
      highlightedItemId: composerHighlightedItemId,
      currentSearchKey: composerMenuSearchKey,
      highlightedSearchKey: composerHighlightedSearchKey,
    });
    return composerMenuItems.find((item) => item.id === activeItemId) ?? null;
  }, [
    composerHighlightedItemId,
    composerHighlightedSearchKey,
    composerMenuItems,
    composerMenuSearchKey,
  ]);

  composerMenuOpenRef.current = composerMenuOpen;
  composerMenuItemsRef.current = composerMenuItems;
  activeComposerMenuItemRef.current = activeComposerMenuItem;

  const nonPersistedComposerImageIdSet = useMemo(
    () => new Set(nonPersistedComposerImageIds),
    [nonPersistedComposerImageIds],
  );

  const planSidebarLabel = t`Tasks`;
  const showPlanSidebarToggle = Boolean(activePlan || planSidebarOpen);
  const composerFooterActionLayoutKey = useMemo(() => {
    if (phase === "running") {
      return `running:${voiceInput.status}:${composerSendState.hasSendableContent}`;
    }
    return `idle:${composerSendState.hasSendableContent}:${isSendBusy}:${isConnecting}:${voiceInput.status}`;
  }, [composerSendState.hasSendableContent, isConnecting, isSendBusy, phase, voiceInput.status]);

  const isComposerMenuLoading =
    composerTriggerKind === "path" && pathTriggerQuery.length > 0 && workspaceEntries.isPending;
  const composerMenuEmptyState = useMemo(() => {
    if (composerTriggerKind === "skill") {
      return t`No skills found. Try / to browse provider commands.`;
    }
    return composerTriggerKind === "path"
      ? t`No matching files or folders.`
      : t`No matching command.`;
  }, [composerTriggerKind, t]);

  const collapsedComposerPrimaryActionDisabled =
    isSendBusy || isConnecting || blocked || !composerSendState.hasSendableContent;
  const collapsedComposerPrimaryActionLabel = t`Send message`;

  // ------------------------------------------------------------------
  // Prompt helpers
  // ------------------------------------------------------------------
  const setPrompt = useCallback(
    (nextPrompt: string) => {
      setComposerDraftPrompt(composerDraftTarget, nextPrompt);
    },
    [composerDraftTarget, setComposerDraftPrompt],
  );

  const addComposerImage = useCallback(
    (image: ComposerImageAttachment) => {
      addComposerDraftImage(composerDraftTarget, image);
    },
    [composerDraftTarget, addComposerDraftImage],
  );

  const addComposerImagesToDraft = useCallback(
    (images: ComposerImageAttachment[]) => {
      addComposerDraftImages(composerDraftTarget, images);
    },
    [composerDraftTarget, addComposerDraftImages],
  );

  const removeComposerImageFromDraft = useCallback(
    (imageId: string) => {
      removeComposerDraftImage(composerDraftTarget, imageId);
    },
    [composerDraftTarget, removeComposerDraftImage],
  );

  const addComposerFileAttachmentsToDraft = useCallback(
    (attachments: ComposerFileAttachment[]) => {
      addComposerDraftFileAttachments(composerDraftTarget, attachments);
    },
    [addComposerDraftFileAttachments, composerDraftTarget],
  );

  const removeComposerFileAttachmentFromDraft = useCallback(
    (attachmentId: string) => {
      removeComposerDraftFileAttachment(composerDraftTarget, attachmentId);
    },
    [composerDraftTarget, removeComposerDraftFileAttachment],
  );

  // ------------------------------------------------------------------
  // Sync refs back to parent
  // ------------------------------------------------------------------
  useEffect(() => {
    promptRef.current = prompt;
    setComposerCursor((existing) => clampCollapsedComposerCursor(prompt, existing));
  }, [prompt, promptRef]);

  useEffect(() => {
    composerImagesRef.current = composerImages;
  }, [composerImages, composerImagesRef]);

  useEffect(() => {
    composerFileAttachmentsRef.current = composerFileAttachments;
  }, [composerFileAttachments, composerFileAttachmentsRef]);

  // ------------------------------------------------------------------
  // Composer menu highlight sync
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!composerMenuOpen) {
      setComposerHighlightedItemId(null);
      setComposerHighlightedSearchKey(null);
      return;
    }
    const nextActiveItemId = resolveComposerMenuActiveItemId({
      items: composerMenuItems,
      highlightedItemId: composerHighlightedItemId,
      currentSearchKey: composerMenuSearchKey,
      highlightedSearchKey: composerHighlightedSearchKey,
    });
    setComposerHighlightedItemId((existing) =>
      existing === nextActiveItemId ? existing : nextActiveItemId,
    );
    setComposerHighlightedSearchKey((existing) =>
      existing === composerMenuSearchKey ? existing : composerMenuSearchKey,
    );
  }, [
    composerHighlightedItemId,
    composerHighlightedSearchKey,
    composerMenuItems,
    composerMenuOpen,
    composerMenuSearchKey,
  ]);

  // ------------------------------------------------------------------
  // Reset compositor state on thread/draft change
  // ------------------------------------------------------------------
  useEffect(() => {
    setComposerHighlightedItemId(null);
    setComposerCursor(collapseExpandedComposerCursor(promptRef.current, promptRef.current.length));
    setComposerTrigger(detectComposerTrigger(promptRef.current, promptRef.current.length));
    dragDepthRef.current = 0;
    setIsDragOverComposer(false);
  }, [draftId, activeThreadId, promptRef]);

  // ------------------------------------------------------------------
  // Footer compact layout observation
  // ------------------------------------------------------------------
  useLayoutEffect(() => {
    const composerForm = composerFormRef.current;
    if (!composerForm) return;
    const measureComposerFormWidth = () => composerForm.clientWidth;
    const measureFooterCompactness = () =>
      shouldUseCompactComposerFooter(measureComposerFormWidth());

    composerFormHeightRef.current = composerForm.getBoundingClientRect().height;
    setIsComposerFooterCompact(measureFooterCompactness());
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;
      const nextFooterCompact = measureFooterCompactness();
      setIsComposerFooterCompact((previous) =>
        previous === nextFooterCompact ? previous : nextFooterCompact,
      );
      const nextHeight = entry.contentRect.height;
      const previousHeight = composerFormHeightRef.current;
      composerFormHeightRef.current = nextHeight;
      if (previousHeight > 0 && Math.abs(nextHeight - previousHeight) < 0.5) return;
      if (!shouldAutoScrollRef.current) return;
      scheduleStickToBottom();
    });

    observer.observe(composerForm);
    return () => {
      observer.disconnect();
    };
  }, [activeThreadId, composerFooterActionLayoutKey, scheduleStickToBottom, shouldAutoScrollRef]);

  // ------------------------------------------------------------------
  // Image persist effect
  // ------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (composerImages.length === 0) {
        clearComposerDraftPersistedAttachments(composerDraftTarget);
        return;
      }
      const getPersistedAttachmentsForThread = () =>
        getComposerDraft(composerDraftTarget)?.persistedAttachments ?? [];
      try {
        const currentPersistedAttachments = getPersistedAttachmentsForThread();
        const existingPersistedById = new Map(
          currentPersistedAttachments.map((attachment) => [attachment.id, attachment]),
        );
        const stagedAttachmentById = new Map<string, PersistedComposerImageAttachment>();
        await Promise.all(
          composerImages.map(async (image) => {
            try {
              const dataUrl = await readFileAsDataUrl(image.file);
              stagedAttachmentById.set(image.id, {
                id: image.id,
                name: image.name,
                mimeType: image.mimeType,
                sizeBytes: image.sizeBytes,
                dataUrl,
              });
            } catch {
              const existingPersisted = existingPersistedById.get(image.id);
              if (existingPersisted) {
                stagedAttachmentById.set(image.id, existingPersisted);
              }
            }
          }),
        );
        const serialized = Array.from(stagedAttachmentById.values());
        if (cancelled) return;
        syncComposerDraftPersistedAttachments(composerDraftTarget, serialized);
      } catch {
        const currentImageIds = new Set(composerImages.map((image) => image.id));
        const fallbackPersistedAttachments = getPersistedAttachmentsForThread();
        const fallbackPersistedIds: Array<string> = [];
        for (const attachment of fallbackPersistedAttachments) {
          if (currentImageIds.has(attachment.id)) {
            fallbackPersistedIds.push(attachment.id);
          }
        }
        const fallbackPersistedIdSet = new Set(fallbackPersistedIds);
        const fallbackAttachments = fallbackPersistedAttachments.filter((attachment) =>
          fallbackPersistedIdSet.has(attachment.id),
        );
        if (cancelled) return;
        syncComposerDraftPersistedAttachments(composerDraftTarget, fallbackAttachments);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    composerDraftTarget,
    clearComposerDraftPersistedAttachments,
    composerImages,
    getComposerDraft,
    syncComposerDraftPersistedAttachments,
  ]);

  // ------------------------------------------------------------------
  // Callbacks: prompt change
  // ------------------------------------------------------------------
  const onPromptChange = useCallback(
    (
      nextPrompt: string,
      nextCursor: number,
      expandedCursor: number,
      cursorAdjacentToMention: boolean,
    ) => {
      promptRef.current = nextPrompt;
      setPrompt(nextPrompt);
      setComposerCursor(nextCursor);
      setComposerTrigger(
        cursorAdjacentToMention ? null : detectComposerTrigger(nextPrompt, expandedCursor),
      );
    },
    [promptRef, setPrompt],
  );

  // ------------------------------------------------------------------
  // Callbacks: prompt replacement / menu
  // ------------------------------------------------------------------
  const applyPromptReplacement = useCallback(
    (
      rangeStart: number,
      rangeEnd: number,
      replacement: string,
      options?: { expectedText?: string; focusEditorAfterReplace?: boolean; cursor?: number },
    ): boolean => {
      const currentText = promptRef.current;
      const safeStart = Math.max(0, Math.min(currentText.length, rangeStart));
      const safeEnd = Math.max(safeStart, Math.min(currentText.length, rangeEnd));
      if (
        options?.expectedText !== undefined &&
        currentText.slice(safeStart, safeEnd) !== options.expectedText
      ) {
        return false;
      }
      const next = replaceTextRange(promptRef.current, rangeStart, rangeEnd, replacement);
      const nextCursor = collapseExpandedComposerCursor(next.text, options?.cursor ?? next.cursor);
      const nextExpandedCursor = expandCollapsedComposerCursor(next.text, nextCursor);
      promptRef.current = next.text;
      setPrompt(next.text);
      setComposerCursor(nextCursor);
      setComposerTrigger(detectComposerTrigger(next.text, nextExpandedCursor));
      if (options?.focusEditorAfterReplace !== false) {
        window.requestAnimationFrame(() => {
          composerEditorRef.current?.focusAt(nextCursor);
        });
      }
      return true;
    },
    [promptRef, setPrompt],
  );

  const readComposerSnapshot = useCallback((): {
    value: string;
    cursor: number;
    expandedCursor: number;
  } => {
    const editorSnapshot = composerEditorRef.current?.readSnapshot();
    if (editorSnapshot) {
      return editorSnapshot;
    }
    return {
      value: promptRef.current,
      cursor: composerCursor,
      expandedCursor: expandCollapsedComposerCursor(promptRef.current, composerCursor),
    };
  }, [composerCursor, promptRef]);

  const resolveActiveComposerTrigger = useCallback((): {
    snapshot: { value: string; cursor: number; expandedCursor: number };
    trigger: ComposerTrigger | null;
  } => {
    const snapshot = readComposerSnapshot();
    return {
      snapshot,
      trigger: detectComposerTrigger(snapshot.value, snapshot.expandedCursor),
    };
  }, [readComposerSnapshot]);

  const onSelectComposerItem = useCallback(
    (item: ComposerCommandItem) => {
      if (composerSelectLockRef.current) return;
      composerSelectLockRef.current = true;
      window.requestAnimationFrame(() => {
        composerSelectLockRef.current = false;
      });
      const { snapshot, trigger } = resolveActiveComposerTrigger();
      if (!trigger) return;
      if (item.type === "path") {
        const replacement = `${serializeComposerFileLink(item.path)} `;
        const replacementRangeEnd = extendReplacementRangeForTrailingSpace(
          snapshot.value,
          trigger.rangeEnd,
          replacement,
        );
        const applied = applyPromptReplacement(
          trigger.rangeStart,
          replacementRangeEnd,
          replacement,
          { expectedText: snapshot.value.slice(trigger.rangeStart, replacementRangeEnd) },
        );
        if (applied) {
          setComposerHighlightedItemId(null);
        }
        return;
      }
      if (item.type === "provider-slash-command") {
        const replacement = `/${item.command.name} `;
        const replacementRangeEnd = extendReplacementRangeForTrailingSpace(
          snapshot.value,
          trigger.rangeEnd,
          replacement,
        );
        const applied = applyPromptReplacement(
          trigger.rangeStart,
          replacementRangeEnd,
          replacement,
          { expectedText: snapshot.value.slice(trigger.rangeStart, replacementRangeEnd) },
        );
        if (applied) {
          setComposerHighlightedItemId(null);
        }
        return;
      }
      if (item.type === "skill") {
        const replacement = `$${item.skill.name} `;
        const replacementRangeEnd = extendReplacementRangeForTrailingSpace(
          snapshot.value,
          trigger.rangeEnd,
          replacement,
        );
        const applied = applyPromptReplacement(
          trigger.rangeStart,
          replacementRangeEnd,
          replacement,
          { expectedText: snapshot.value.slice(trigger.rangeStart, replacementRangeEnd) },
        );
        if (applied) {
          setComposerHighlightedItemId(null);
        }
        return;
      }
    },
    [applyPromptReplacement, resolveActiveComposerTrigger],
  );

  const onComposerMenuItemHighlighted = useCallback(
    (itemId: string | null) => {
      setComposerHighlightedItemId(itemId);
      setComposerHighlightedSearchKey(composerMenuSearchKey);
    },
    [composerMenuSearchKey],
  );

  const nudgeComposerMenuHighlight = useCallback(
    (key: "ArrowDown" | "ArrowUp") => {
      if (composerMenuItems.length === 0) return;
      const highlightedIndex = composerMenuItems.findIndex(
        (item) => item.id === composerHighlightedItemId,
      );
      const normalizedIndex =
        highlightedIndex >= 0 ? highlightedIndex : key === "ArrowDown" ? -1 : 0;
      const offset = key === "ArrowDown" ? 1 : -1;
      const nextIndex =
        (normalizedIndex + offset + composerMenuItems.length) % composerMenuItems.length;
      const nextItem = composerMenuItems[nextIndex];
      setComposerHighlightedItemId(nextItem?.id ?? null);
    },
    [composerHighlightedItemId, composerMenuItems],
  );

  const blurMobileComposerAfterSend = useCallback(() => {
    if (!isMobileViewport) return;
    if (composerBlurFrameRef.current !== null) {
      window.cancelAnimationFrame(composerBlurFrameRef.current);
      composerBlurFrameRef.current = null;
    }
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
      activeElement.blur();
    }
    setIsComposerFocused(false);
  }, [isMobileViewport]);

  const shouldBlurMobileComposerOnSubmit = useCallback(() => {
    if (!isMobileViewport) return false;
    if (isSendBusy || isConnecting) return false;
    return composerSendState.hasSendableContent;
  }, [composerSendState.hasSendableContent, isConnecting, isMobileViewport, isSendBusy]);

  const sendComposer = useCallback(
    (event?: { preventDefault: () => void }) => {
      onSend(event);
      if (shouldBlurMobileComposerOnSubmit()) {
        blurMobileComposerAfterSend();
      }
    },
    [blurMobileComposerAfterSend, onSend, shouldBlurMobileComposerOnSubmit],
  );
  const sendComposerRef = useRef(sendComposer);
  useEffect(() => {
    sendComposerRef.current = sendComposer;
  }, [sendComposer]);

  const insertVoiceTranscript = useCallback(
    (transcript: string, options?: { focusEditorAfterReplace?: boolean }): boolean => {
      const snapshot = readComposerSnapshot();
      const insertion = insertTranscriptAtCursor(
        snapshot.value,
        snapshot.expandedCursor,
        transcript,
      );
      if (!insertion) return false;
      return applyPromptReplacement(insertion.insertAt, insertion.insertAt, insertion.replacement, {
        cursor: insertion.cursor,
        ...options,
      });
    },
    [applyPromptReplacement, readComposerSnapshot],
  );

  const sendAfterVoiceRef = useRef(false);
  const voiceTargetKeyRef = useRef(voiceTargetKey);
  useEffect(() => {
    voiceTargetKeyRef.current = voiceTargetKey;
    sendAfterVoiceRef.current = false;
  }, [voiceTargetKey]);

  useEffect(() => {
    let earlierClipInFlight = false;
    for (const clip of composerVoiceClips) {
      if (clip.status === "recording" || clip.status === "transcribing") {
        earlierClipInFlight = true;
        continue;
      }
      if (clip.status !== "transcribed" || earlierClipInFlight) continue;
      const targetKey = clip.targetKey;
      void voiceClips.consumeTranscript(clip.id, (transcript) => {
        if (voiceTargetKeyRef.current !== targetKey) return false;
        return insertVoiceTranscript(transcript, {
          focusEditorAfterReplace: !sendAfterVoiceRef.current,
        });
      });
    }
    if (!sendAfterVoiceRef.current) return;
    if (composerVoiceClips.some((clip) => clip.status === "failed")) {
      sendAfterVoiceRef.current = false;
      return;
    }
    if (composerVoiceClips.length === 0 && voiceInput.status === "idle") {
      sendAfterVoiceRef.current = false;
      sendComposerRef.current();
    }
  }, [composerVoiceClips, insertVoiceTranscript, voiceInput.status]);

  const voiceClipStatusesRef = useRef(new Map<string, VoiceClip["status"]>());
  const announcedVoiceClipFailuresRef = useRef(new Set<string>());
  useEffect(() => {
    const previous = voiceClipStatusesRef.current;
    const next = new Map<string, VoiceClip["status"]>();
    for (const clip of composerVoiceClips) {
      next.set(clip.id, clip.status);
      if (clip.status !== "failed") continue;
      const previousStatus = previous.get(clip.id);
      const shouldAnnounce =
        previousStatus === undefined
          ? !announcedVoiceClipFailuresRef.current.has(clip.id)
          : previousStatus !== "failed";
      if (!shouldAnnounce) continue;
      announcedVoiceClipFailuresRef.current.add(clip.id);
      toastManager.add(
        clip.failure?.kind === "tooLong"
          ? {
              type: "warning",
              title: t`Recording stopped`,
              description: t`Recordings are limited to 30 minutes.`,
            }
          : { type: "error", title: t`Could not transcribe the audio`, description: t`Try again.` },
      );
    }
    voiceClipStatusesRef.current = next;
  }, [composerVoiceClips, t]);

  const submitComposer = useCallback(
    (event?: { preventDefault: () => void }) => {
      if (voiceInput.status === "recording") {
        event?.preventDefault();
        sendAfterVoiceRef.current = true;
        void voiceInput.stop();
        return;
      }
      if (voiceInput.status !== "idle") {
        event?.preventDefault();
        return;
      }
      if (composerVoiceClips.length > 0) {
        event?.preventDefault();
        sendAfterVoiceRef.current = true;
        for (const clip of composerVoiceClips) {
          if (clip.status === "pending" || clip.status === "failed") {
            void voiceClips.transcribe(clip.id);
          }
        }
        return;
      }
      sendComposer(event);
    },
    [composerVoiceClips, sendComposer, voiceInput.status, voiceInput.stop],
  );

  const hasPendingVoiceClips = composerVoiceClips.length > 0;
  const voiceInputControls = useMemo<ComposerVoiceInputControls>(
    () => ({
      status: voiceInput.status,
      hasPendingClips: hasPendingVoiceClips,
      availability: resolveVoiceInputAvailability(voiceInput.supported, voiceTranscriptionEnabled),
      disabled: isConnecting || inputUnavailable,
      onStart: voiceInput.start,
      onStop: () => void voiceInput.stop(),
    }),
    [
      inputUnavailable,
      hasPendingVoiceClips,
      isConnecting,
      voiceInput.start,
      voiceInput.status,
      voiceInput.stop,
      voiceInput.supported,
      voiceTranscriptionEnabled,
    ],
  );
  const voiceBarClip = composerVoiceClips[0] ?? null;
  const voiceBarMode: ComposerVoiceBarMode =
    voiceInput.status === "recording"
      ? "recording"
      : voiceBarClip?.status === "failed" || voiceBarClip?.status === "pending"
        ? "recorded"
        : "transcribing";
  const voiceBarAudio = useVoiceClipAudio(voiceBarMode === "recorded" ? voiceBarClip : null);
  const isVoiceBarActive = voiceBarMode === "recording" || voiceBarClip !== null;
  const cancelVoiceBar = useCallback(() => {
    if (voiceInput.status === "recording") {
      voiceInput.cancel();
      return;
    }
    if (!voiceBarClip) return;
    sendAfterVoiceRef.current = false;
    void voiceClips.discard(voiceBarClip.id);
  }, [voiceBarClip, voiceInput]);
  const expandMobileComposer = useCallback(() => {
    if (composerBlurFrameRef.current !== null) {
      window.cancelAnimationFrame(composerBlurFrameRef.current);
      composerBlurFrameRef.current = null;
    }
    if (mobileComposerExpandFrameRef.current !== null) {
      window.cancelAnimationFrame(mobileComposerExpandFrameRef.current);
    }
    if (mobileComposerExpandReleaseFrameRef.current !== null) {
      window.cancelAnimationFrame(mobileComposerExpandReleaseFrameRef.current);
    }
    mobileComposerExpandInFlightRef.current = true;
    setIsComposerFocused(true);
    mobileComposerExpandFrameRef.current = window.requestAnimationFrame(() => {
      mobileComposerExpandFrameRef.current = null;
      composerEditorRef.current?.focusAtEnd();
      mobileComposerExpandReleaseFrameRef.current = window.requestAnimationFrame(() => {
        mobileComposerExpandReleaseFrameRef.current = null;
        mobileComposerExpandInFlightRef.current = false;
      });
    });
  }, []);

  // ------------------------------------------------------------------
  // Callbacks: command key
  // ------------------------------------------------------------------
  const onComposerCommandKey = (
    key: "ArrowDown" | "ArrowUp" | "Enter" | "Tab",
    event: KeyboardEvent,
  ) => {
    const { trigger } = resolveActiveComposerTrigger();
    const menuIsActive = composerMenuOpenRef.current || trigger !== null;
    if (menuIsActive) {
      const currentItems = composerMenuItemsRef.current;
      const selectedItem = activeComposerMenuItemRef.current ?? currentItems[0];
      if (key === "ArrowDown" && currentItems.length > 0) {
        nudgeComposerMenuHighlight("ArrowDown");
        return true;
      }
      if (key === "ArrowUp" && currentItems.length > 0) {
        nudgeComposerMenuHighlight("ArrowUp");
        return true;
      }
      if ((key === "Enter" || key === "Tab") && selectedItem) {
        onSelectComposerItem(selectedItem);
        return true;
      }
    }
    if (key === "Enter" && !event.shiftKey) {
      submitComposer();
      return true;
    }
    return false;
  };

  // ------------------------------------------------------------------
  // Callbacks: attachments
  // ------------------------------------------------------------------
  const addComposerAttachments = (files: File[]) => {
    if (!activeThreadId || blocked || files.length === 0) return;
    const nextImages: ComposerImageAttachment[] = [];
    const nextFiles: ComposerFileAttachment[] = [];
    let nextAttachmentCount =
      composerImagesRef.current.length + composerFileAttachmentsRef.current.length;
    let error: string | null = null;
    for (const file of files) {
      if (file.size > PROVIDER_SEND_TURN_MAX_FILE_BYTES) {
        error = t`'${file.name}' exceeds the ${FILE_SIZE_LIMIT_LABEL} attachment limit.`;
        continue;
      }
      if (nextAttachmentCount >= PROVIDER_SEND_TURN_MAX_ATTACHMENTS) {
        error = t`You can attach up to ${PROVIDER_SEND_TURN_MAX_ATTACHMENTS} files per message.`;
        break;
      }
      if (file.type.startsWith("image/") && file.size <= PROVIDER_SEND_TURN_MAX_IMAGE_BYTES) {
        const previewUrl = URL.createObjectURL(file);
        nextImages.push({
          type: "image",
          id: randomUUID(),
          name: file.name || "image",
          mimeType: file.type,
          sizeBytes: file.size,
          previewUrl,
          file,
        });
      } else {
        nextFiles.push({
          type: "file",
          id: randomUUID(),
          name: file.name || "attachment",
          mimeType: file.type,
          sizeBytes: file.size,
          file,
        });
        if (file.type.startsWith("image/")) {
          error = t`'${file.name}' exceeds the ${IMAGE_SIZE_LIMIT_LABEL} image preview limit and will be stored as a file.`;
        }
      }
      nextAttachmentCount += 1;
    }
    if (nextImages.length === 1 && nextImages[0]) {
      addComposerImage(nextImages[0]);
    } else if (nextImages.length > 1) {
      addComposerImagesToDraft(nextImages);
    }
    if (nextFiles.length > 0) {
      addComposerFileAttachmentsToDraft(nextFiles);
    }
    setThreadError(activeThreadId, error);
  };

  const removeComposerImage = (imageId: string) => {
    removeComposerImageFromDraft(imageId);
  };

  const removeComposerFileAttachment = (attachmentId: string) => {
    removeComposerFileAttachmentFromDraft(attachmentId);
  };

  // ------------------------------------------------------------------
  // Callbacks: file picker / paste / drag
  // ------------------------------------------------------------------
  const openAttachFilesPicker = () => {
    attachFilesInputRef.current?.click();
  };

  const onAttachFilesInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const files = Array.from(input.files ?? []);
    input.value = "";
    addComposerAttachments(files);
    focusComposer();
  };

  const onComposerPaste = (event: React.ClipboardEvent<HTMLElement>) => {
    const files = Array.from(event.clipboardData.files);
    if (files.length === 0) return;
    event.preventDefault();
    addComposerAttachments(files);
  };

  const onComposerDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    if (blocked || !event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    dragDepthRef.current += 1;
    setIsDragOverComposer(true);
  };

  const onComposerDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (blocked || !event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    setIsDragOverComposer(true);
  };

  const onComposerDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDragOverComposer(false);
    }
  };

  const onComposerDrop = (event: React.DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    dragDepthRef.current = 0;
    setIsDragOverComposer(false);
    const files = Array.from(event.dataTransfer.files);
    addComposerAttachments(files);
    focusComposer();
  };
  const handleInterruptPrimaryAction = useCallback(() => {
    void onInterrupt();
  }, [onInterrupt]);
  const scheduleComposerCollapseCheck = useCallback(() => {
    if (!isMobileViewport) {
      return;
    }
    if (mobileComposerExpandInFlightRef.current) {
      return;
    }
    if (composerBlurFrameRef.current !== null) {
      window.cancelAnimationFrame(composerBlurFrameRef.current);
    }
    composerBlurFrameRef.current = window.requestAnimationFrame(() => {
      composerBlurFrameRef.current = null;
      if (mobileComposerExpandInFlightRef.current) {
        return;
      }
      const composerSurface = composerSurfaceRef.current;
      const activeElement = document.activeElement;
      if (activeElement instanceof Element && isInsideComposerFloatingLayer(activeElement)) {
        return;
      }
      if (
        composerSurface &&
        activeElement instanceof Node &&
        composerSurface.contains(activeElement)
      ) {
        return;
      }
      setIsComposerFocused(false);
    });
  }, [isMobileViewport]);

  useEffect(() => {
    return () => {
      if (composerBlurFrameRef.current !== null) {
        window.cancelAnimationFrame(composerBlurFrameRef.current);
      }
      if (mobileComposerExpandFrameRef.current !== null) {
        window.cancelAnimationFrame(mobileComposerExpandFrameRef.current);
      }
      if (mobileComposerExpandReleaseFrameRef.current !== null) {
        window.cancelAnimationFrame(mobileComposerExpandReleaseFrameRef.current);
      }
    };
  }, []);

  // ------------------------------------------------------------------
  // Imperative handle
  // ------------------------------------------------------------------
  useImperativeHandle(
    composerRef,
    () => ({
      focusAtEnd: () => {
        composerEditorRef.current?.focusAtEnd();
      },
      focusAt: (cursor: number) => {
        composerEditorRef.current?.focusAt(cursor);
      },
      insertTextAtEnd: (text: string) => {
        if (text.length === 0 || isConnecting || inputUnavailable) {
          return false;
        }
        const rangeEnd = promptRef.current.length;
        return applyPromptReplacement(rangeEnd, rangeEnd, text);
      },
      replacePrompt: (text: string) => {
        if (isConnecting || inputUnavailable) {
          return false;
        }
        return applyPromptReplacement(0, promptRef.current.length, text);
      },
      readSnapshot: () => {
        return readComposerSnapshot();
      },
      resetCursorState: (options?: {
        cursor?: number;
        prompt?: string;
        detectTrigger?: boolean;
      }) => {
        const promptForState = options?.prompt ?? promptRef.current;
        const cursor = clampCollapsedComposerCursor(promptForState, options?.cursor ?? 0);
        setComposerHighlightedItemId(null);
        setComposerCursor(cursor);
        setComposerTrigger(
          options?.detectTrigger
            ? detectComposerTrigger(
                promptForState,
                expandCollapsedComposerCursor(promptForState, cursor),
              )
            : null,
        );
      },
      getSendContext: () => ({
        prompt: promptRef.current,
        images: composerImagesRef.current,
        files: composerFileAttachmentsRef.current,
        deepWork: deepWorkFeatureEnabled && deepWorkEnabled,
      }),
    }),
    [
      promptRef,
      composerImagesRef,
      composerFileAttachmentsRef,
      isConnecting,
      inputUnavailable,
      applyPromptReplacement,
      readComposerSnapshot,
      deepWorkEnabled,
      deepWorkFeatureEnabled,
    ],
  );

  // Render
  // ------------------------------------------------------------------
  return (
    <>
      <form
        ref={composerFormRef}
        onSubmit={submitComposer}
        className="mx-auto w-full min-w-0 max-w-208"
        data-chat-composer-form="true"
      >
        <input
          ref={attachFilesInputRef}
          type="file"
          multiple
          className="hidden"
          data-chat-composer-attach-input="true"
          onChange={onAttachFilesInputChange}
        />
        <div
          className="group rounded-[22px] p-px transition-colors duration-200"
          onDragEnter={onComposerDragEnter}
          onDragOver={onComposerDragOver}
          onDragLeave={onComposerDragLeave}
          onDrop={onComposerDrop}
        >
          <div
            ref={composerSurfaceRef}
            data-chat-composer-mobile-collapsed={isComposerCollapsedMobile ? "true" : "false"}
            className={cn(
              "rounded-[20px] border bg-card transition-colors duration-200 has-focus-visible:border-ring/45",
              isDragOverComposer ? "border-primary/70 bg-accent/30" : "border-border",
              inputUnavailable ? "opacity-75" : null,
            )}
            onFocusCapture={(event) => {
              const activeElement = event.target;
              if (
                isComposerCollapsedMobile &&
                activeElement instanceof HTMLElement &&
                activeElement.closest('[data-chat-composer-collapsed-controls="true"]')
              ) {
                return;
              }
              if (composerBlurFrameRef.current !== null) {
                window.cancelAnimationFrame(composerBlurFrameRef.current);
                composerBlurFrameRef.current = null;
              }
              setIsComposerFocused(true);
            }}
            onBlurCapture={() => {
              scheduleComposerCollapseCheck();
            }}
          >
            {isComposerCollapsedMobile ? (
              <div className="flex items-center justify-between gap-2 px-3 py-2">
                <button
                  type="button"
                  className={cn(
                    "min-w-0 flex-1 truncate bg-transparent p-0 text-left text-[14px] focus:outline-none",
                    prompt.trim() ? "text-foreground" : "text-muted-foreground/35",
                  )}
                  onPointerDown={(event) => event.preventDefault()}
                  onClick={expandMobileComposer}
                  aria-label={t`Expand composer`}
                >
                  {prompt.trim() || t`Describe a task or ask a question`}
                </button>
                <button
                  type="button"
                  className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/90 text-primary-foreground disabled:opacity-30"
                  disabled={collapsedComposerPrimaryActionDisabled}
                  aria-label={collapsedComposerPrimaryActionLabel}
                  onPointerDown={(event) => event.preventDefault()}
                  onClick={(event) => {
                    event.stopPropagation();
                    submitComposer();
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path
                      d="M8 3L8 13M8 3L4 7M8 3L12 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            ) : null}

            <div
              className={cn(
                "relative px-3 pt-3.5 pb-2 sm:px-4 sm:pt-4",
                isComposerCollapsedMobile && "hidden",
              )}
            >
              {composerMenuOpen && (
                <div className="absolute inset-x-0 bottom-full z-20 mb-2">
                  <ComposerCommandMenu
                    items={composerMenuItems}
                    resolvedTheme={resolvedTheme}
                    isLoading={isComposerMenuLoading}
                    triggerKind={composerTriggerKind}
                    emptyStateText={composerMenuEmptyState}
                    activeItemId={activeComposerMenuItem?.id ?? null}
                    onHighlightedItemChange={onComposerMenuItemHighlighted}
                    onSelect={onSelectComposerItem}
                  />
                </div>
              )}

              {!isComposerCollapsedMobile &&
                (composerImages.length > 0 || composerFileAttachments.length > 0) && (
                  <AttachmentsBlock
                    attachments={[...composerImages, ...composerFileAttachments]}
                    variant="composer"
                    onExpandImage={onExpandImage}
                    onRemove={(attachment) =>
                      attachment.type === "image"
                        ? removeComposerImage(attachment.id)
                        : removeComposerFileAttachment(attachment.id)
                    }
                    unpersistedImageIds={nonPersistedComposerImageIdSet}
                  />
                )}

              <div className="relative">
                <ComposerPromptEditor
                  editorRef={composerEditorRef}
                  value={prompt}
                  cursor={composerCursor}
                  skills={agentStatus?.skills ?? []}
                  onChange={onPromptChange}
                  onCommandKeyDown={onComposerCommandKey}
                  onPaste={onComposerPaste}
                  placeholder={
                    environmentUnavailable
                      ? environmentUnavailable.connectionState === "connecting"
                        ? t`${environmentUnavailable.label} is connecting`
                        : t`${environmentUnavailable.label} is disconnected`
                      : t`Describe a task or ask a question`
                  }
                  disabled={isConnecting || inputUnavailable}
                />
              </div>
            </div>

            {/* Bottom toolbar */}
            {isComposerCollapsedMobile ? null : (
              <div
                data-chat-composer-footer="true"
                data-chat-composer-footer-compact={isComposerFooterCompact ? "true" : "false"}
                className={cn(
                  "flex min-w-0 flex-nowrap items-center justify-between gap-2 overflow-visible px-2.5 pb-2.5 sm:px-3 sm:pb-3",
                  isComposerFooterCompact ? "gap-1.5" : "gap-2 sm:gap-0",
                )}
              >
                {isVoiceBarActive ? (
                  <ComposerVoiceRecordingBar
                    mode={voiceBarMode}
                    stream={voiceInput.stream}
                    audio={voiceBarAudio}
                    durationMs={voiceBarClip?.durationMs ?? null}
                    theme={resolvedTheme}
                    onCancel={cancelVoiceBar}
                  />
                ) : (
                  <div className="-m-1 flex min-w-0 flex-1 items-center gap-1 overflow-x-auto p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    <ComposerAttachFilesButton
                      disabled={activeThreadId === null || blocked}
                      onClick={openAttachFilesPicker}
                    />
                    {isComposerFooterCompact ? (
                      <CompactComposerControlsMenu
                        activePlan={showPlanSidebarToggle}
                        planSidebarLabel={planSidebarLabel}
                        planSidebarOpen={planSidebarOpen}
                        showDeepWorkToggle={deepWorkFeatureEnabled}
                        deepWorkEnabled={deepWorkEnabled}
                        onToggleDeepWork={requestToggleDeepWork}
                        onTogglePlanSidebar={togglePlanSidebar}
                      />
                    ) : (
                      <>
                        <ComposerFooterModeControls
                          showPlanToggle={showPlanSidebarToggle}
                          planSidebarLabel={planSidebarLabel}
                          planSidebarOpen={planSidebarOpen}
                          showDeepWorkToggle={deepWorkFeatureEnabled}
                          deepWorkEnabled={deepWorkEnabled}
                          onToggleDeepWork={requestToggleDeepWork}
                          onTogglePlanSidebar={togglePlanSidebar}
                        />
                      </>
                    )}
                  </div>
                )}

                {/* Right side: send / stop button */}
                <div
                  data-chat-composer-actions="right"
                  className="flex shrink-0 flex-nowrap items-center justify-end gap-2"
                >
                  <ComposerFooterPrimaryActions
                    activeContextWindow={isVoiceBarActive ? null : activeContextWindow}
                    isRunning={phase === "running"}
                    isSendBusy={isSendBusy}
                    isConnecting={isConnecting}
                    isEnvironmentUnavailable={environmentUnavailable !== null}
                    isBlocked={blocked}
                    isUploading={isUploading}
                    hasSendableContent={composerSendState.hasSendableContent}
                    voiceInput={voiceInputEnabled ? voiceInputControls : null}
                    preserveComposerFocusOnPointerDown={isMobileViewport}
                    onInterrupt={handleInterruptPrimaryAction}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </form>
      <AlertDialog open={deepWorkIntroOpen} onOpenChange={setDeepWorkIntroOpen}>
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Enable Deep Work?</Trans>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>
                Deep Work mode searches and analyzes materials more thoroughly. It is recommended
                for the most complex tasks and can take a long time.
              </Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>
              <Trans>Cancel</Trans>
            </AlertDialogClose>
            <Button onClick={confirmDeepWorkIntro}>
              <Trans>Enable</Trans>
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </>
  );
});
